import { jsxs, jsx } from "react/jsx-runtime";
import { styled } from "@linaria/react";
import { useRef, useState, useLayoutEffect, useEffect, useCallback } from "react";
import { typographyClassName, elevationClassName } from "@creditornot/cb-ingredients";
import { createCssVars, clsx, assignInlineCssVars } from "@creditornot/cb-utils";
import "./style.css";
const sizeCssVars = createCssVars(["height", "fontSize", "borderRadius"]);
const Root = /* @__PURE__ */ styled("div")({
  name: "Root",
  class: "cb_SegmentedControl_Root_ryu7",
  propsAsIs: false
});
const FocusIndicator = /* @__PURE__ */ styled("div")({
  name: "FocusIndicator",
  class: "cb_SegmentedControl_FocusIndicator_f1og",
  propsAsIs: false
});
const ItemLabel = /* @__PURE__ */ styled("div")({
  name: "ItemLabel",
  class: "cb_SegmentedControl_ItemLabel_i1xj",
  propsAsIs: false
});
const ItemOverlayLabelContainer = /* @__PURE__ */ styled("div")({
  name: "ItemOverlayLabelContainer",
  class: "cb_SegmentedControl_ItemOverlayLabelContainer_i140",
  propsAsIs: false
});
const ItemOverlayLabel = /* @__PURE__ */ styled("div")({
  name: "ItemOverlayLabel",
  class: "cb_SegmentedControl_ItemOverlayLabel_i1wc",
  propsAsIs: false
});
const Segment = /* @__PURE__ */ styled("div")({
  name: "Segment",
  class: "cb_SegmentedControl_Segment_smbd",
  propsAsIs: false
});
const _exp21 = () => ({
  $left
}) => `${$left}px`;
const _exp22 = () => ({
  $width
}) => `${$width}px`;
const _exp23 = () => ({
  $duration
}) => `left ${$duration} ease-in-out, width ${$duration} ease-in-out`;
const SegmentBackground = /* @__PURE__ */ styled("div")({
  name: "SegmentBackground",
  class: "cb_SegmentedControl_SegmentBackground_sqvk",
  propsAsIs: false,
  vars: {
    "sqvkiil-0": [_exp21()],
    "sqvkiil-1": [_exp22()],
    "sqvkiil-2": [_exp23()]
  }
});
const SegmentedControl = ({
  className,
  disabled,
  fixedWidths,
  onChange,
  options,
  style,
  styleParams,
  value
}) => {
  const activeEl = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [bgState, setBgState] = useState({
    left: 0,
    width: 0,
    duration: "0ms"
  });
  useLayoutEffect(() => {
    if (activeEl.current) {
      const {
        offsetLeft: left,
        offsetWidth: width
      } = activeEl.current;
      setBgState({
        left,
        width,
        duration: isFirstRender ? "0ms" : "200ms"
      });
      if (isFirstRender) {
        setIsFirstRender(false);
      }
    }
  }, [value, fixedWidths]);
  useEffect(() => {
    if (!fixedWidths || !window)
      return;
    const handleResize = () => {
      if (activeEl.current) {
        const {
          offsetLeft: left,
          offsetWidth: width
        } = activeEl.current;
        setBgState({
          left,
          width,
          duration: "0ms"
        });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [fixedWidths]);
  const handleChange = useCallback((newValue) => {
    if (!disabled && newValue !== value && onChange) {
      onChange(newValue);
    }
  }, [value, disabled, onChange]);
  return (
    /* @__PURE__ */ jsxs(Root, {
      "aria-disabled": disabled || void 0,
      className: clsx(className, typographyClassName.bodyBase),
      "data-fixed-widths": fixedWidths,
      role: "group",
      style: {
        ...(styleParams ? assignInlineCssVars(sizeCssVars, styleParams) : null),
        ...style
      },
      children: [options.map(({
        disabled: disabledOption,
        label,
        value: optionValue
      }, index) => {
        var _a;
        return /* @__PURE__ */ jsxs(Segment, {
          "aria-disabled": disabled || disabledOption ? true : void 0,
          "aria-pressed": value === optionValue,
          "data-active": value === optionValue,
          "data-fixed-widths": fixedWidths,
          "data-show-divider": value !== optionValue && ((_a = options[index - 1]) == null ? void 0 : _a.value) !== value && index > 0,
          onClick: () => {
            if (!disabledOption)
              handleChange(optionValue);
          },
          onKeyDown: (event) => {
            if (["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"].includes(event.key)) {
              event.preventDefault();
              const enabledOptions = options.filter((option) => !option.disabled);
              const index2 = enabledOptions.findIndex((option) => option.value === optionValue);
              const newIndex = event.key === "ArrowRight" || event.key === "ArrowDown" ? index2 + 1 : index2 - 1;
              if (newIndex >= 0 && newIndex < enabledOptions.length) {
                handleChange(enabledOptions[newIndex].value);
                setTimeout(() => {
                  var _a2;
                  (_a2 = activeEl.current) == null ? void 0 : _a2.focus();
                });
              }
            }
            if (event.key === " ") {
              event.preventDefault();
              handleChange(optionValue);
              setTimeout(() => {
                var _a2;
                (_a2 = activeEl.current) == null ? void 0 : _a2.focus();
              });
            }
          },
          ref: value === optionValue ? activeEl : null,
          role: "button",
          tabIndex: disabledOption ? -1 : 0,
          children: [/* @__PURE__ */ jsx(ItemLabel, {
            children: label
          }), /* @__PURE__ */ jsx(ItemOverlayLabelContainer, {
            "aria-hidden": true,
            "data-show": value === optionValue,
            children: /* @__PURE__ */ jsx(ItemOverlayLabel, {
              children: label
            })
          }), /* @__PURE__ */ jsx(FocusIndicator, {})]
        }, label);
      }), /* @__PURE__ */ jsx(SegmentBackground, {
        $duration: bgState.duration,
        $left: bgState.left,
        $width: bgState.width,
        className: elevationClassName.xsmall
      })]
    })
  );
};
const SegmentedControl$1 = SegmentedControl;
export {
  SegmentedControl$1 as SegmentedControl
};
//# sourceMappingURL=index.js.map
